/**
 * 通用 API 类型 + 错误码枚举 + web-feedback 业务类型
 *
 * 数据来源：
 * - 通用类型：w1-prd.md 第 3.4 节
 * - web-feedback 业务字段：doc/wb-routes.md 第 3、4 节（基于后端 commit 6fbe71b）
 *
 * 重要：web-feedback 后端**不**使用 ApiResponse 包装，直接返回业务对象；
 * 错误时使用 `detail: { code, message, suggestion }` 结构（见 wb-routes.md 第 7.4 节）。
 *
 * 命名约定（audit M-7，2026-08-13）：
 * - API 字段保持 snake_case（与后端 Python 一致）：doc_id / history_id / task_id /
 *   created_at / overall_score 等。前端 TS interface 直接暴露 snake_case 字段，
 *   避免在 API 边界做映射带来字段对齐成本（与老前端 cloud-sync / history-ui 保持一致）。
 * - 前端业务代码（Vue setup / composable / store）用 camelCase：
 *   currentTaskId / historyId / pollTimer 等。
 * - 仅在「字段从 API 出来后立即被业务消费」处转 camelCase（如 props 透传）。
 * - JS / TS 不强制 snake_case 警告，仅在 PR review 时人工校验。
 */

/** 统一响应包装（仅用于宽松模式判定，后端目前不发此结构） */
export interface ApiResponse<T = unknown> {
  code: number; // 0=成功，非 0=业务错误
  message: string;
  data: T;
}

/**
 * 兼容宽松模式判定：web-feedback 老后端很多接口直接返回业务对象，
 * 不带 {code,message,data} 包装。当响应体不含 code 字段时按成功处理，
 * data 取 response.data 本身。W2 后端整体改造后再收紧。
 */
export function isWrappedApiResponse(body: unknown): body is ApiResponse {
  return (
    typeof body === 'object' &&
    body !== null &&
    'code' in body &&
    typeof (body as { code: unknown }).code === 'number'
  );
}

/** 业务错误码枚举（按需扩充） */
export enum ApiErrorCode {
  SUCCESS = 0,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  RATE_LIMITED = 429,
  SERVER_ERROR = 500,
  /** 邀请码无效或已停用 */
  INVITE_CODE_INVALID = 4001,
  /** 试用次数耗尽 */
  TRIAL_EXHAUSTED = 4002,
  /** 任务不存在或无权访问 */
  TASK_NOT_FOUND = 4003,
  /** Provider 调用失败（LLM 上游错误） */
  PROVIDER_ERROR = 5001,
}

/**
 * 后端 401 / 403 错误响应的 detail 结构（wb-routes.md 第 7.4 节）。
 *
 * ```json
 * { "detail": { "code": "NO_AUTH", "message": "未登录", "suggestion": "..." } }
 * ```
 */
export interface ApiErrorDetail {
  /** 字符串形式的错误码（如 'NO_AUTH' / 'INVALID_INVITE_CODE' / 'TRIAL_EXHAUSTED'） */
  code: string;
  /** 用户可读的错误说明（中文） */
  message: string;
  /** 后端给出的修复建议（中文，可空） */
  suggestion?: string;
}

/** HTTP 错误类（携带状态码 + 后端 body） */
export class HttpError extends Error {
  status?: number;
  body?: unknown;
  code?: number;

  constructor(message: string, opts: { status?: number; body?: unknown; code?: number } = {}) {
    super(message);
    this.name = 'HttpError';
    this.status = opts.status;
    this.body = opts.body;
    this.code = opts.code;
  }
}

// ===== web-feedback 业务类型（基于 wb-routes.md 第 3、4 节真实字段）=====
//
// 重要约定：
// - 所有字段命名严格对齐后端 Pydantic 响应模型（snake_case）
// - 不假设字段，未在 wb-routes.md 中明确的字段用 `[key: string]: unknown` 兜底
// - 后端不使用 ApiResponse 外层包装，方法返回类型即业务对象

/** 用户认证类型 */
export type WbAuthType = 'invite' | 'github' | 'admin';

/** 用户角色 */
export type WbUserRole = 'admin' | 'user';

/**
 * web-feedback 用户对象。
 *
 * 来自 `GET /api/auth/me`（wb-routes.md 第 3.2 节）。
 * 字段说明：
 * - `id`：用户唯一标识（后端是 int，序列化为 JSON 后按 number 处理）
 * - `auth_type`：登录方式（邀请码 / GitHub / 管理员账号）
 * - `trial_used` / `trial_quota`：已用配额 / 总配额
 *   - trial_remaining 由前端按 `trial_quota - trial_used` 计算（stores/auth.ts getter）
 *   - trial_quota = -1 表示无限
 */
export interface WbUser {
  id: number;
  auth_type: WbAuthType;
  name: string | null;
  email: string | null;
  created_at: string; // ISO 字符串
  role: WbUserRole;
  is_active: boolean;
  trial_used: number;
  trial_quota: number;
}

/**
 * 邀请码登录响应（POST /api/auth/invite）。
 *
 * 来自 wb-routes.md 第 3.1 节：`{ token, user: { id, auth_type, name, email } }`。
 *
 * 注意：后端实际响应里的 `user` 字段仅含 `id / auth_type / name / email` 四个字段，
 * 但 TypeScript 层把 `user` 标为完整 `WbUser` 类型，方便调用方（如 stores/auth.ts）
 * 把响应直接写入 `auth.user` state，后续 `fetchMe()` 会拉完整字段补齐。
 *
 * 即：登录后短暂窗口内 user 字段是不完整的（只有 4 个），fetchMe 后补全。如果调用方
 * 在 fetchMe 完成前访问 `created_at / role / trial_used` 等字段会拿到 undefined，
 * 应避免在登录态未稳定时读取这些字段（路由守卫已用 meStatus 阻塞）。
 */
export interface WbAuthResp {
  token: string;
  user: WbUser;
}

/**
 * 管理员账号密码登录响应（POST /api/auth/admin-login，wb-routes.md 第 4.6 节）。
 *
 * - `redirect` 固定为 `/admin.html`（老前端跳转用，portal-frontend 内部走 vue-router 不消费此字段）
 * - `user` 仅含 id + username
 */
export interface WbAdminLoginResp {
  token: string;
  redirect: string;
  user: { id: number; username: string };
}

/** 注销响应（POST /api/auth/logout） */
export interface WbLogoutResp {
  ok: boolean;
}

// ===== 用户数据云同步（替换老 historyApi）=====
//
// 后端 wb-routes.md 第 3.6 节：web-feedback 没有 /api/history，历史记录通过
// /api/user/data* 整体同步实现。前端在 localStorage 维护 history 数组。

/**
 * 历史记录条目（前端 localStorage 自维护，整体 PUT/merge 到 /api/user/data）。
 *
 * 字段命名沿用老前端 submit-flow.js:559-604 writeHistoryFromRender（已写入
 * localStorage 的数据兼容），W1-T5c 由 view 层组装此对象。
 *
 * EXP-004 修复（2026-08-10）：补全老前端 14 字段对齐，让历史回看时
 * studyPlanContextReady=true（按钮可点）。
 *
 * 字段来源对照（老前端 submit-flow.js:564-596）：
 * - id / created_at：时间戳
 * - provider / model：来自 stage2Data（LLM provider 信息）
 * - jd_summary：JD 前 60 字（老前端字段，保留兼容）
 * - jd_text_full：JD 完整文本（老前端字段，保留兼容）
 * - jd：Portal 自加字段（500 字截断，回填 textarea 用，老前端不读此字段）
 * - job_title：stage1Data.requirements.job_title
 * - overall_score / level / dimensions / gaps：从 stage2Data.match 提取
 *   （老 entry 缺 match 时，onApplyHistory 用这 4 字段重组 match）
 * - score：Portal 现有字段（与 overall_score 同义，给 RecentStrip / 列表展示用）
 * - match：stage2Data.match 完整对象（含 dimensions/gaps/...，contextReady 关键字段）
 * - requirements：stage1Data.requirements 完整对象（contextReady 关键字段）
 * - favorited / raw_markdown：固定字段
 * - resume_text：paste / upload 已解析时的简历文本（contextReady 关键字段）
 * - use_default_resume：default 模式标志，值为 'true'（字符串，contextReady 关键字段）
 */
export interface WbHistoryEntry {
  id: string;
  created_at: number; // unix ms（前端生成）
  provider?: string;
  model?: string;
  /** 老前端字段：JD 前 60 字摘要（保留兼容，recent-strip 展示） */
  jd_summary?: string;
  /** 老前端字段：JD 完整文本（保留兼容，老前端切回时可读） */
  jd_text_full?: string;
  /** Portal 现有字段：JD 截断（500 字），回填 textarea 用 */
  jd?: string;
  job_title: string;
  /** 老前端字段：从 match.overall_score 提取（entry 缺 match 时重组用） */
  overall_score?: number;
  /** 老前端字段：从 match.level 提取（entry 缺 match 时重组用） */
  level?: string;
  /** 老前端字段：从 match.dimensions 提取（entry 缺 match 时重组用） */
  dimensions?: unknown[];
  /** 老前端字段：从 match.gaps 提取（学习计划按钮依赖） */
  gaps?: string[];
  /** Portal 现有字段：与 overall_score 同义，给列表 / RecentStrip 展示用 */
  score?: number;
  /** stage2Data.match 完整对象（含 dimensions/gaps/overall_score/level） */
  match?: Record<string, unknown>;
  /** stage1Data.requirements 完整对象 */
  requirements?: Record<string, unknown>;
  favorited?: boolean;
  raw_markdown?: string;
  /** paste / upload 已解析的简历文本（contextReady 关键字段） */
  resume_text?: string;
  /** default 模式标志，字符串 'true'（contextReady 关键字段） */
  use_default_resume?: string;
  [key: string]: unknown;
}

/**
 * 学习计划存储结构（key 是 history_id，value 是学习计划 markdown / 对象）。
 *
 * 来自 wb-routes.md 第 3.6 节 UserDataResponse.study_plans。
 */
export type WbStudyPlanMap = Record<string, unknown>;

/** GET /api/user/data 完整响应 */
export interface WbUserData {
  history: WbHistoryEntry[];
  study_plans: WbStudyPlanMap;
  resumes: unknown[];
  updated_at: string | null;
}

/** PUT /api/user/data 入参（None 字段保留原值） */
export interface WbUserDataUpdate {
  history?: WbHistoryEntry[];
  study_plans?: WbStudyPlanMap;
  resumes?: unknown[];
}

/** POST /api/user/data/merge 入参（client 覆盖 server） */
export interface WbUserDataMerge {
  history: WbHistoryEntry[];
  study_plans?: WbStudyPlanMap;
  resumes?: unknown[];
}

// ===== 配额查询 =====

/** 配额类型（GET /api/user/quota，wb-routes.md 第 4.4 节） */
export type WbQuotaType = 'invite' | 'unlimited' | 'none';

export interface WbUserQuota {
  quota_type: WbQuotaType;
  /** 总配额（-1 表示无限） */
  total: number;
  used: number;
  remaining: number;
  warning_threshold: number;
}

// ===== JD 分析（两段串行模型）=====
//
// 关键校准（wb-routes.md 第 6.2 节）：
// - stage1 入参字段是 `jd_text`（不是 `jd`），强制 multipart，不传简历
// - stage2 入参必须传 `requirements_json`（stage1 返回的 requirements 对象 JSON 字符串）+ 简历来源三选一
// - 两段都强制 multipart/form-data

/**
 * Stage1 拆解岗位要求响应（POST /api/analyze/requirements）。
 *
 * 来自 wb-routes.md 第 3.4 节：`RequirementsResponse`。
 * - `requirements` 字段是后端的 `ConditionAnalysis` 对象（硬技能 / 软性 / 项目 / 年限），具体子字段
 *   W1-T5b 由 view 层对照真实接口再补；类型层先按 unknown 兜底。
 * - 若 stage1 调用时传了 `task_id`，响应额外含 `task_id` 字段（用于断点续跑）。
 */
export interface WbStage1Resp {
  provider: string;
  model: string;
  requirements: Record<string, unknown>;
  warnings: string[];
  task_id?: string;
  [key: string]: unknown;
}

/**
 * Stage2 匹配分析响应（POST /api/analyze/match）。
 *
 * 来自 wb-routes.md 第 3.5 节：`MatchResponse`。
 */
export interface WbStage2Resp {
  provider: string;
  model: string;
  match: Record<string, unknown>;
  interview_questions: unknown[];
  raw_markdown: string;
  warnings: string[];
  task_id?: string;
  [key: string]: unknown;
}

/**
 * 任务状态（GET /api/analyze/status/:task_id 轮询用，wb-routes.md 第 4.1 节）。
 *
 * 后端 status 取值：'running' | 'done' | 'failed' | 'requirements_done'。
 * （PRD 早期假设的 'pending' / 'succeeded' 不准确，已校准）
 */
export interface WbTaskStatus {
  task_id: string;
  status: 'running' | 'done' | 'failed' | 'requirements_done';
  elapsed_seconds?: number;
  result?: Record<string, unknown>;
  requirements_response?: Record<string, unknown>;
  error?: string;
  [key: string]: unknown;
}

// ===== 文件上传 / OCR / 简历解析 =====

/**
 * OCR / resume/parse 共享响应结构（wb-routes.md 第 4.3 节）。
 * - `text`：解析出的纯文本
 * - `chars`：字符数
 */
export interface WbTextExtractResp {
  text: string;
  chars: number;
  [key: string]: unknown;
}

/** OCR 兼容别名（与老 API 名一致） */
export type WbOcrResp = WbTextExtractResp;

/** 简历解析兼容别名 */
export type WbResumeResp = WbTextExtractResp;

/** OCR 可用性（GET /api/analyze/ocr/availability，wb-routes.md 第 4.3 节） */
export interface WbOcrAvailability {
  available: boolean;
}

// ===== 学习计划 =====

/**
 * 学习计划资源类型（对齐 web-feedback/app/schemas.py:121 StudyResource.type）。
 * - doc / video / book / course / repo / other
 */
export type WbStudyResourceType = 'doc' | 'video' | 'book' | 'course' | 'repo' | 'other';

/** 学习资源（对齐 StudyResource schema） */
export interface WbStudyResource {
  title: string;
  type?: WbStudyResourceType;
  url?: string;
  note?: string;
  [key: string]: unknown;
}

/** 学习任务（对齐 StudyTask schema） */
export interface WbStudyTask {
  name: string;
  detail?: string;
  estimated_hours?: number;
  [key: string]: unknown;
}

/** 单周计划（对齐 StudyWeek schema，week_index 限定 1-4） */
export interface WbStudyWeek {
  week_index: number;
  theme: string;
  tasks: WbStudyTask[];
  resources: WbStudyResource[];
  acceptance: string[];
  [key: string]: unknown;
}

/** 30 天学习计划（对齐 StudyPlan schema：4 周 + 30-120 小时） */
export interface WbStudyPlan {
  goal: string;
  weeks: WbStudyWeek[];
  estimated_hours_total: number;
  acceptance_summary: string;
  [key: string]: unknown;
}

/**
 * 学习计划响应（POST /api/analyze/study-plan，wb-routes.md 第 4.2 节）。
 *
 * 注意：返回的是 `StudyPlanResponse`，不是老 PRD 假设的 `{ plan_id, markdown }`。
 * 同时该路由会写入 `UserData.study_plans[history_id]`。
 */
export interface WbStudyPlanResp {
  provider: string;
  model: string;
  plan: WbStudyPlan;
  warnings: string[];
  [key: string]: unknown;
}

// ===== Provider 配置 =====

/**
 * Provider 配置响应（GET /api/providers，wb-routes.md 第 4.8 节）。
 *
 * 注意：`default` 是 JS 保留字，访问时需用字符串 key（`resp['default']`）。
 */
export interface WbProvidersResp {
  default: string;
  available: string[];
  has_default_resume: boolean;
  [key: string]: unknown;
}

/** Landing 配置（Portal Footer 用） */
export interface WbLandingConfig {
  author_name?: string;
  author_role?: string;
  author_email?: string;
  author_github?: string;
  author_zhihu?: string;
  /** 版权文案（landing.html:257 用 data.copyright，home.html:251 硬编码 © 2026 lihanlin.cn） */
  copyright?: string;
  icp?: string;
  [key: string]: unknown;
}

// ===== Admin 邀请码管理 =====

/**
 * 邀请码对象（wb-routes.md 第 4.5 节）。
 *
 * 注意 `code` 字段是 UUID hex / `WFB-XXXXXX` 字符串，**不是数字 id**。
 *
 * 字段说明（对齐老前端 invite-codes.js 实际消费字段）：
 * - `analyze_used`：已用次数（老前端 invite-codes.js:264/271/278 直接 Number(c.analyze_used)）
 * - `bound_user_id`：当前绑定的用户 id（invite-codes.js:223 c.bound_user_id；
 *   与 `used_by` 并存，新前端做了 bound_user_id → used_by 兜底）
 * - `revoked_at`：撤销时间（部分后端响应会带）
 */
export interface WbInviteCode {
  code: string;
  name?: string;
  is_active: boolean;
  analyze_quota: number;
  /** 已用分析次数（与 analyze_quota 组合算进度条；老前端 invite-codes.js:264） */
  analyze_used?: number;
  expires_at: string | null;
  created_at: string;
  /** 当前绑定用户 id（老前端 invite-codes.js:223 优先读此字段，否则回退 used_by） */
  bound_user_id?: number | null;
  used_by?: number | null;
  used_at?: string | null;
  /** 撤销时间（部分后端响应会带，保留兼容） */
  revoked_at?: string | null;
  [key: string]: unknown;
}

/** 创建邀请码入参（POST /api/admin/invite-codes，超管 lihanlin 独占） */
export interface WbInviteCodeCreatePayload {
  name?: string;
  analyze_quota?: number; // 默认 10
  expires_at?: string; // ISO 字符串
}

/** 更新邀请码入参（PATCH /api/admin/invite-codes/{code}） */
export interface WbInviteCodeUpdatePayload {
  name?: string;
  analyze_quota?: number;
  expires_at?: string;
  is_active?: boolean;
}

/** GET /api/admin/invite-codes 响应（包裹结构，不是裸数组） */
export interface WbInviteCodeListResp {
  total: number;
  codes: WbInviteCode[];
}

/** 撤销邀请码响应（DELETE /api/admin/invite-codes/{code}） */
export interface WbInviteCodeRevokeResp {
  ok: boolean;
  is_active: boolean;
}

// ===== Admin 账号管理（admin-accounts，区别于 admin/users）=====

/**
 * 管理员账号对象（wb-routes.md 第 4.5 节「账号管理」）。
 *
 * 字段说明（对齐老前端 admin-accounts.js 实际消费字段）：
 * - `user_id`：关联的 wb_user 主键 id（admin-accounts.js:187 data-attribute +
 *   197 防自杀比较 Number(acc.user_id) === currentUid）
 * - `revoked_at`：撤销时间戳（admin-accounts.js:190 状态判定优先级「撤销 > 停用 > 活跃」）
 */
export interface WbAdminAccount {
  id: number;
  username: string;
  is_active: boolean;
  /** 关联 wb_user.id（admin-accounts.js:187 data-userId） */
  user_id?: number | null;
  created_at?: string;
  last_login_at?: string | null;
  /** 撤销时间（admin-accounts.js:190 三态判定的关键字段，已撤销行整体半透明 + 删除线） */
  revoked_at?: string | null;
  [key: string]: unknown;
}

/** GET /api/admin/admin-accounts 响应（包裹结构） */
export interface WbAdminAccountListResp {
  total: number;
  accounts: WbAdminAccount[];
}

/** 创建管理员账号入参（POST /api/admin/admin-accounts） */
export interface WbAdminAccountCreatePayload {
  username: string;
  password: string;
  is_active?: boolean; // 默认 true
}

/** 更新管理员账号入参（PATCH /api/admin/admin-accounts/{account_id}） */
export interface WbAdminAccountUpdatePayload {
  is_active?: boolean;
  password?: string;
}

/** 撤销管理员账号响应（DELETE /api/admin/admin-accounts/{account_id}） */
export interface WbAdminAccountRevokeResp {
  ok: boolean;
  revoked_at: string;
  is_active: boolean;
}

// ===== Admin 用户管理（admin/users，与 admin-accounts 不同）=====

/**
 * 用户对象（admin/users 系列，wb-routes.md 第 4.5 节「用户管理」）。
 *
 * 与 WbUser 的差异：admin 列表场景字段更全（含 created_at 等）。
 */
export interface WbAdminUser {
  id: number;
  auth_type: WbAuthType;
  name: string | null;
  email: string | null;
  role: WbUserRole;
  is_active: boolean;
  trial_used: number;
  trial_quota: number;
  created_at?: string;
  [key: string]: unknown;
}

/** GET /api/admin/users 响应（包裹结构） */
export interface WbAdminUserListResp {
  total: number;
  users: WbAdminUser[];
}

/** PUT /api/admin/users/{user_id} 入参（admin 强制 + 防提权 + 防锁死） */
export interface WbAdminUserUpdatePayload {
  is_active?: boolean;
  trial_quota?: number;
  role?: WbUserRole;
}

/** POST /api/admin/users/{user_id}/reset-trial 响应 */
export interface WbAdminUserResetTrialResp {
  ok: boolean;
  trial_used: number;
}

/** GET /api/admin/stats 响应 */
export interface WbAdminStats {
  total_users: number;
  active_users: number;
  trial_near_limit: number;
  [key: string]: unknown;
}

// ===== Admin 访问统计（GET /api/admin/visit-stats?days=N）=====

/** 每日 UV/PV（date 为 UTC+8 字符串，前端直接展示，不再做时区换算） */
export interface WbVisitDailyItem {
  date: string;
  uv: number;
  pv: number;
}

/** TOP 访问路径 */
export interface WbVisitPathItem {
  path: string;
  count: number;
}

/** TOP 访问 IP */
export interface WbVisitIpItem {
  ip: string;
  count: number;
}

/** GET /api/admin/visit-stats 响应（recent 最近请求明细已按用户决策移除） */
export interface WbVisitStats {
  today: { uv: number; pv: number };
  total: { uv: number; pv: number };
  daily: WbVisitDailyItem[];
  top_paths: WbVisitPathItem[];
  top_ips: WbVisitIpItem[];
  [key: string]: unknown;
}
